import { PrestationQuestionType } from '@cpce/database/enums';
import {
  type AskedQuestion,
  type PrestationQuestionData,
  prestationQuestionDataSchema,
} from '@cpce/shared';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash } from 'lucide-react';
import { useState } from 'react';
import {
  useCreatePrestationQuestion,
  useRemovePrestationQuestion,
  useReorderPrestationQuestions,
  useUpdatePrestationQuestion,
} from '@/api/prestations/prestation-questions.mutations';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { CardWithTitle } from '@/components/CardWithTitle';
import { ConfirmButton } from '@/components/ConfirmButton';
import { FieldLabel } from '@/components/form/FieldLabel';
import { FormCheckbox } from '@/components/form/FormCheckbox';
import { FormCombobox } from '@/components/form/FormCombobox';
import { FormInput } from '@/components/form/FormInput';
import { FormModal } from '@/components/form/FormModal';
import { ModalButton } from '@/components/ModalButton';
import { useZodForm } from '@/hooks/useZodForm';

const QUESTION_TYPE_LABELS: Record<PrestationQuestionType, string> = {
  text: 'Texte',
  number: 'Nombre',
  amount: 'Montant (€)',
  date: 'Date',
  boolean: 'Oui / non',
  choice: 'Liste de choix',
};

const QUESTION_TYPE_OPTIONS = Object.values(PrestationQuestionType).map((type) => ({
  value: type,
  label: QUESTION_TYPE_LABELS[type],
}));

interface PrestationQuestionsProps {
  prestationId: number;
  questions: AskedQuestion[];
}

export function PrestationQuestions({ prestationId, questions }: PrestationQuestionsProps) {
  const reorder = useReorderPrestationQuestions(prestationId);
  const remove = useRemovePrestationQuestion(prestationId);

  const move = (index: number, offset: -1 | 1) => {
    const ids = questions.map((question) => question.id);
    [ids[index], ids[index + offset]] = [ids[index + offset], ids[index]];
    reorder.mutate(ids);
  };

  return (
    <CardWithTitle
      title="Questions pour l'inscription"
      actions={
        <ModalButton
          button={(openModal) => (
            <Button variant="color" icon={Plus} onClick={openModal}>
              Ajouter une question
            </Button>
          )}
          modal={(open, closeModal) =>
            open && <QuestionModal prestationId={prestationId} closeModal={closeModal} />
          }
        />
      }
    >
      {questions.length === 0 ? (
        <p className="text-sm text-gray-500">Aucune question configurée pour l'inscription.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-200">
          {questions.map((question, index) => (
            <li
              key={question.id}
              className="flex items-center justify-between gap-4 py-2"
              aria-label={question.label}
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {question.label}
                  <Badge>{QUESTION_TYPE_LABELS[question.type]}</Badge>
                  {question.required && <Badge>Obligatoire</Badge>}
                </div>
                {question.type === PrestationQuestionType.choice && (
                  <span className="text-xs text-gray-500">{question.options.join(' · ')}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  icon={ArrowUp}
                  className="w-8 h-8"
                  title="Monter"
                  disabled={index === 0 || reorder.isPending}
                  onClick={() => move(index, -1)}
                />
                <Button
                  variant="outline"
                  icon={ArrowDown}
                  className="w-8 h-8"
                  title="Descendre"
                  disabled={index === questions.length - 1 || reorder.isPending}
                  onClick={() => move(index, 1)}
                />
                <ModalButton
                  button={(openModal) => (
                    <Button
                      variant="outline"
                      icon={Pencil}
                      className="w-8 h-8"
                      title="Modifier"
                      onClick={openModal}
                    />
                  )}
                  modal={(open, closeModal) =>
                    open && (
                      <QuestionModal
                        prestationId={prestationId}
                        question={question}
                        closeModal={closeModal}
                      />
                    )
                  }
                />
                <ConfirmButton
                  button={(openModal) => (
                    <Button
                      variant="outline"
                      color="red"
                      icon={Trash}
                      className="w-8 h-8"
                      title="Retirer"
                      onClick={openModal}
                    />
                  )}
                  confirmationDescription={`La question « ${question.label} » ne sera plus posée à l'inscription. Si des dossiers y ont déjà répondu, elle est archivée et leurs réponses restent visibles ; sinon elle est supprimée.`}
                  confirmationButtonLabel="Retirer la question"
                  onConfirm={async () => {
                    await remove.mutate(question.id);
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </CardWithTitle>
  );
}

interface QuestionModalProps {
  prestationId: number;
  question?: AskedQuestion;
  closeModal: () => void;
}

function QuestionModal({ prestationId, question, closeModal }: QuestionModalProps) {
  const create = useCreatePrestationQuestion(prestationId);
  const update = useUpdatePrestationQuestion(prestationId);
  const [optionsText, setOptionsText] = useState(question?.options.join('\n') ?? '');
  const [defaultValues] = useState<PrestationQuestionData>(() => ({
    label: question?.label ?? '',
    type: question?.type ?? PrestationQuestionType.text,
    required: question?.required ?? false,
    options: question?.options ?? [],
  }));

  const form = useZodForm<PrestationQuestionData>({
    schema: prestationQuestionDataSchema,
    defaultValues,
    onSubmit: async (data) => {
      if (question) await update.mutate({ questionId: question.id, data });
      else await create.mutate(data);
      closeModal();
    },
  });
  const isChoice = form.watch('type') === PrestationQuestionType.choice;
  const optionsError = form.formData.errors.options;

  return (
    <FormModal
      title={question ? 'Modifier la question' : 'Ajouter une question'}
      open
      onOpenChange={(open) => !open && closeModal()}
      onSubmit={form.submit}
      resetForm={form.resetForm}
      isPending={create.isPending || update.isPending}
      submitLabel={question ? 'Enregistrer' : 'Ajouter'}
    >
      <FormInput label="Intitulé" fieldName="label" formProps={form.formData} />
      <FormCombobox
        label="Type de réponse"
        fieldName="type"
        formProps={form.formData}
        options={QUESTION_TYPE_OPTIONS}
      />
      {isChoice && (
        <FieldLabel
          label="Choix proposés (un par ligne)"
          error={optionsError?.message ?? optionsError?.root?.message}
        >
          <textarea
            value={optionsText}
            onChange={(event) => {
              setOptionsText(event.target.value);
              form.setValue(
                'options',
                event.target.value
                  .split('\n')
                  .map((option) => option.trim())
                  .filter(Boolean)
              );
            }}
            className="min-h-24 rounded-md border border-gray-300 px-3 py-2 text-sm w-full"
          />
        </FieldLabel>
      )}
      <FormCheckbox label="Réponse obligatoire" fieldName="required" formProps={form.formData} />
    </FormModal>
  );
}
